<script setup lang="ts">
import './object_picker.scss'
import { APIClient } from '@/onenetwork/core/onenetwork.api.instant.ts'
import _ from 'lodash'
import { computed, ref, toRaw, watch } from 'vue'
import { uuid } from 'vue3-uuid'
import { Icon } from '@iconify/vue'
import { regions } from '@/onenetwork/constants/onenetwork.regions.const'

interface Props {
  placeholder?: string,
  type?: string,
  multiple?: boolean,
  modelValue?: any,
  apiUrl: string,
  queryField: string,
  key_field: string,
  display_field: string,
  default_filter_params?: Record<string, any>,
  readOnly?: boolean,
  hidePreview?: boolean,
  data?: any,
  displayComp?: any,
}

const props = defineProps<Props>()
const emit = defineEmits(['update:modelValue', 'update:selected'])

const keywordInput = ref<HTMLInputElement>(null)
const queryResultPanel = ref<HTMLUListElement>(null)
const showQueryPanel = ref(false)
const previewValue = ref('')
const selectedValue = ref<any>(props.modelValue || [])
const queryResult = ref<any>(props?.data || [])
const selectedIndex = ref(-1)
const loading = ref(false)
const readOnly = computed(() => props.readOnly)

watch(() => props.modelValue, (newVal) => {

  selectedValue.value = newVal || []
})

const handleClickOutside = (e: MouseEvent) => {
  if( !keywordInput.value?.contains(e.target as Node) ) {

    showQueryPanel.value = false
    selectedIndex.value = -1
  }
}

const handleClick = () => {

  showQueryPanel.value = true
  selectedIndex.value = -1
  queryResult.value = parseResult(props?.data || [])
}

const parseResult = (rawData: any) => {
  return rawData.map((item: any) => {
    return {
      ...item,
      id: item[props.key_field],
      name: item[props.display_field],
      select: false
    }
  })
}

const normalizeText = (value: string) => {
  return (value || '')
    .toLowerCase()
    .trim()
}

const requestFetchQueryResult = async (keyword: string) => {
  try{
    loading.value = true
    if(!props.apiUrl){
      const normalizedKeyword = normalizeText(keyword)
      const rest  = (props?.data || []).filter((item: any) => {
        const normalizedName = normalizeText(item?.name || '')
        return normalizedName.includes(normalizedKeyword)
      })
      queryResult.value = parseResult(rest)
    }else{

      const res  = await APIClient.get(props.apiUrl, {
        params: {
          [props.queryField]: keyword,
        }
      })

      if(res?.data?.data?.data?.length > 0){

        queryResult.value = parseResult(res.data.data.data)
      }
    }

  } catch (e) {

  } finally {
    loading.value = false
  }
}

const debouncedRequest = _.debounce((value: string) => {

  requestFetchQueryResult(value);
}, 500);


const moveSelection = (direction: 'up' | 'down') => {
  const list = queryResult.value;

  let index = list.findIndex(item => item.select);

  if (index === -1) {
    index = direction === 'down' ? 0 : list.length - 1;
  } else {
    list[index].select = false;

    index = direction === 'up'
      ? (index - 1 + list.length) % list.length
      : (index + 1) % list.length;
  }

  list[index].select = true;

  selectedIndex.value = index;
  queryResult.value = list;
};


const handleQuery = (e: KeyboardEvent) => {
  if(!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) {
    const target = e.target as HTMLInputElement;
    const value = target?.value || '';

    selectedIndex.value = -1

    if (value.length > 0) {
      debouncedRequest(value);
    } else {
      queryResult.value = parseResult(props?.data || [])
    }
  }
  else{
    switch(e.key) {
      case 'ArrowDown': moveSelection('down'); break;
      case 'ArrowUp': moveSelection('up'); break;
      case 'Enter': handleSelect(); break;
    }
  }
};

const handleSelect = (item: any | undefined = undefined) => {

  if (item) {
    changeSelectedValue(item)
    showQueryPanel.value = false
    return
  }

  if (selectedIndex.value < 0 || !queryResult.value[selectedIndex.value]) {
    return
  }

  const _item = queryResult.value[selectedIndex.value]

  changeSelectedValue(_item)
  showQueryPanel.value = false
}


const changeSelectedValue = (_item: any | undefined = undefined) => {

  if (!_item) return;
  if (props.multiple) {
    const exists = selectedValue.value.some((v: any) => v.id === _item.id);
    if (!exists) {
      selectedValue.value.push(_item);
    }
  } else {
    selectedValue.value = [_item];
  }
  emit('update:selected', toRaw(selectedValue.value));
  emit('update:modelValue', toRaw(selectedValue.value));
}

const removeSelectedValue = (_item: any | undefined = undefined) => {

  if (!_item) return;
  selectedValue.value = selectedValue.value.filter((v: any) => v.id !== _item.id);
  emit('update:selected', toRaw(selectedValue.value));
  emit('update:modelValue', toRaw(selectedValue.value));
};


</script>

<template>
  <div class="object-picker">
    <div ref="keywordInput" :class="`object-picker__preview flex gap-2 ${selectedValue.length > 0 ? 'has-value' : ''}`" @click="handleClick">
      <template v-if="selectedValue.length > 0 && !props.hidePreview">
        <div class="object-picker__preview_item flex" v-for="(item, idx) in selectedValue" :key="`tag-item-${idx}`">
          <span class="name">
            {{ item.name }}
          </span>
          <span class="close" @click="()=>removeSelectedValue(item)" v-if="!readOnly">
            <Icon icon="material-symbols:close" />
          </span>
        </div>
      </template>
      <template v-else>
        <div class="object-picker__preview_item flex w-full">
          <span>{{ props.placeholder || 'Chọn đối tượng' }}</span>
        </div>
      </template>
    </div>
    <div class="object-picker__query " v-if="!readOnly && showQueryPanel" v-click-outside="handleClickOutside">
      <div class="flex flex-col">
        <span class="text-[11px] font-italic">Tìm kiếm:</span>
        <input type="text" class="object-picker__query_keyword" @keyup="handleQuery"/>
      </div>
      <div class="flex flex-col mt-3 overflow-y-auto max-h-[300px]">
        <span class="text-[11px] font-italic">Kết quả:</span>
        <ul class="object-picker__query_result" ref="queryResultPanel" v-if="queryResult.length > 0">
          <li v-for="(item, idx) in queryResult" :key="`item-query-result-${idx}`" :class="item.select ? 'selected' : ''" @click="()=>handleSelect(item)">
            <template v-if="props.displayComp">
              <component :is="props.displayComp" :item="item"/>
            </template>
            <template v-else>
              <span class="name">
                <code>{{item.id}}</code> - <strong>{{item.name}}</strong>
              </span>
            </template>
          </li>
        </ul>
        <div class="flex w-full justify-center text-[#dadada]" v-else>
          <template v-if="loading">Đang tìm kiếm...</template>
          <template v-else>Không có kết quả phù hợp.</template>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
